import { LicenseCancelForm } from "@/components/license-cancel-form";
import PageHeader from "@/components/page-header";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { getLicenseDetailsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useReturnToPreviousContext } from "@/hooks/use-return-to-previous";
import {
  getQueryErrorHeading,
  getQueryErrorMessage,
  isCancelableForOperatorOrGovernment,
} from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { validate as uuidValidate } from "uuid";

export const Route = createFileRoute("/operator/licenses/$licenseId/cancel")({
  component: CancelLicenseScreen,
});

function CancelLicenseScreen() {
  const { licenseId } = Route.useParams();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const isValidLicenseId = uuidValidate(licenseId);
  const { returnToPrevious } = useReturnToPreviousContext();

  const {
    data,
    isLoading: isQueryLoading,
    error: queryError,
  } = useQuery({
    ...getLicenseDetailsOptions({
      path: {
        license_id: licenseId,
      },
    }),
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const handleSuccess = () => {
    returnToPrevious();
  };

  if (!isValidLicenseId) {
    return (
      <ErrorComponent
        title="Invalid URL"
        message={`The license ID '${licenseId}' is not a valid license ID`}
      />
    );
  }
  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }
  if (isQueryLoading) {
    return <LoadingScreen message="Fetching license..." />;
  }
  if (queryError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(queryError)}
        message={getQueryErrorMessage(queryError)}
      />
    );
  }

  if (!data || !isCancelableForOperatorOrGovernment(data.status)) {
    return (
      <ErrorComponent
        title="License not cancelable"
        message="Cancellation is not available for REJECTED, CANCELLED, or PAYMENT_PROCESSING licenses."
      />
    );
  }

  return (
    <div className="flex flex-col container mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Cancel Registration"
        subtitle="Cancel this registration due to a change in circumstances/details"
        onBack={returnToPrevious}
      />
      <div>
        <LicenseCancelForm
          licenseId={licenseId}
          licenseTypeName={data.license_type.name}
          propertyAddress={data.property?.display_address}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
}
